import type { BucketAggregate } from "verdi-common/schemas/time-series/data-stream-query";
import type { ListDataStreams200Item } from "@/api/orval/generated/schemas";
import type { CardRelation } from "../ChartSpec";
import { isVisibleUpTo, type StreamVisibility } from "./stream-visibility";

/** Ephemeral generator output — id, display name, unitCategory, aggregate. See ARCHITECTURE.md § Spec Type Flow. */
export interface ResolvedStream {
    id: number;
    unitCategory: string;
    name: string;
    /**
     * The sensor cluster's friendly name, e.g. "TreeTag". Two streams off one device in one unit
     * category are identical without it. Null when no fragment names one.
     */
    sensorClusterName: string | null;
    /**
     * The sensor element's name, e.g. "Wind Gust". Two channels on one cluster can land in the same
     * unit category, and this is all that tells them apart. Null when no fragment names one.
     */
    sensorElementName: string | null;
    /** The zones the stream's latest fragment sits in. The legend heads its rows with them. */
    zoneNames: string[];
    /** How the stream relates to the card the viewer sits on. Absent reads as the card's own. */
    cardRelation?: CardRelation;
    /**
     * How this stream's buckets are read: its `preferredBucketAggregate`, with the API's "no
     * preference" (`null`) resolved to `mean` here so nothing downstream repeats the fallback.
     */
    aggregate: BucketAggregate;
}

/** Hide ePlant TreeTag inclination (lean) channels for now — dropped on every graph, not just the zone view. */
const EXCLUDED_SENSOR_ELEMENT_NAMES = new Set(["Inclination Angle", "Inclination Direction"]);

export function isPlottableStream(stream: ListDataStreams200Item, visibleUpTo: StreamVisibility): boolean {
    return !EXCLUDED_SENSOR_ELEMENT_NAMES.has(stream.sensorElementName ?? "") && isVisibleUpTo(stream, visibleUpTo);
}

export type ZoneNameLookup = (zoneId: string) => string | undefined;

export function toResolvedStream(stream: ListDataStreams200Item, zoneName: ZoneNameLookup): ResolvedStream {
    return {
        id: stream.id,
        name: stream.hostName ?? `Stream ${stream.id}`,
        sensorClusterName: stream.sensorClusterName,
        sensorElementName: stream.sensorElementName,
        zoneNames: (stream.latestFragment?.zoneIds ?? []).flatMap((zoneId) => zoneName(zoneId) ?? []),
        unitCategory: stream.unitCategory,
        aggregate: stream.preferredBucketAggregate ?? "mean",
    };
}

/** A zone card's stream: its own when the zone's list holds it, and any other zones it sits in. */
export function zoneCardRelation(
    stream: ListDataStreams200Item,
    zoneId: string,
    isZoneStream: boolean,
    zoneName: ZoneNameLookup,
): CardRelation {
    const otherZoneIds = (stream.latestFragment?.zoneIds ?? []).filter((id) => id !== zoneId);
    const cardZoneName = zoneName(zoneId);
    return {
        own: isZoneStream,
        otherZoneNames: otherZoneIds.flatMap((id) => zoneName(id) ?? []),
        hostNamedByCard: false,
        ...(cardZoneName ? { cardZoneName } : {}),
    };
}

/** A device card's stream: always the card's own, and the card's title names the device. Its legend shows no zones. */
export const DEVICE_CARD_RELATION: CardRelation = Object.freeze({
    own: true,
    otherZoneNames: [],
    hostNamedByCard: true,
});

/**
 * Map API streams to ResolvedStream — host label from list-API hostName, otherwise "Stream {id}".
 * unitCategory is passed through for panel grouping; the aggregate decides how buckets are fetched.
 */
export function resolveStreams(
    streams: ListDataStreams200Item[],
    zoneName: ZoneNameLookup,
    visibleUpTo: StreamVisibility,
): ResolvedStream[] {
    return streams
        .filter((stream) => isPlottableStream(stream, visibleUpTo))
        .map((stream) => toResolvedStream(stream, zoneName));
}
